import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { LogOut, LayoutDashboard, Building2, KeyRound, ShieldCheck, Sun, Moon, Boxes, FileCog, Rocket, type LucideIcon } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { useTokenRefresh } from "@/auth/useTokenRefresh";
import { useTheme } from "@/theme/ThemeContext";
import { cn } from "@/lib/utils";

// App-Shell des License-Portals: dunkle Sidebar + Topbar (Muster admin-ui, ohne
// Live-Problem-Badges — das Portal hat keine Agent-Phasen).
type NavEntry = { to: string; label: string; icon: LucideIcon; end?: boolean };
const NAV_ITEMS: NavEntry[] = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/customers", label: "Kunden", icon: Building2 },
  { to: "/licenses", label: "Lizenzen", icon: KeyRound },
  // #343 Provisionierung
  { to: "/cells", label: "Zellen", icon: Boxes },
  { to: "/customer-specs", label: "Spezifikationen", icon: FileCog },
  { to: "/releases", label: "Releases", icon: Rocket },
  { to: "/admins", label: "Admins", icon: ShieldCheck },
];

const TITLES: Record<string, string> = {
  "": "Dashboard",
  customers: "Kunden",
  licenses: "Lizenzen",
  cells: "Zellen",
  "customer-specs": "Kundenspezifikationen",
  releases: "Releases",
  admins: "Portal-Admins",
};

function PortalLogo() {
  return (
    <div className="grid h-[30px] w-[30px] shrink-0 place-items-center rounded-[8px] bg-gradient-to-br from-accent to-[#7c93f5] shadow-[0_2px_8px_rgba(58,85,217,.4)]">
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2 4 6v6c0 5 3.5 8 8 10 4.5-2 8-5 8-10V6l-8-4Z" />
        <path d="m9 12 2 2 4-4" />
      </svg>
    </div>
  );
}

function NavRow({ item }: { item: NavEntry }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]/g, "")}`}
      className={({ isActive }) =>
        cn(
          "group relative mb-0.5 flex items-center gap-3 rounded-[8px] px-2.5 py-2 text-[13.5px] transition-colors",
          isActive
            ? "bg-sidebar-active font-semibold text-sidebar-active-text"
            : "font-medium text-sidebar-text hover:bg-white/5",
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive && <span className="absolute -left-3 bottom-2 top-2 w-[3px] rounded bg-accent" />}
          <Icon className={cn("h-[17px] w-[17px]", isActive ? "text-accent" : "")} />
          <span className="flex-1">{item.label}</span>
        </>
      )}
    </NavLink>
  );
}

export function AppLayout() {
  const { session, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  useTokenRefresh();

  const seg = pathname.split("/")[1] ?? "";
  const title = TITLES[seg] ?? "License Portal";

  function handleLogout() {
    logout();
    navigate("/login", { replace: true });
  }

  return (
    <div className="flex min-h-screen bg-bg">
      {/* ---------- Sidebar ---------- */}
      <aside className="sticky top-0 flex h-screen w-[244px] shrink-0 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-text">
        <div className="flex items-center gap-3 px-[18px] pb-3.5 pt-[18px]">
          <PortalLogo />
          <div className="leading-[1.15]">
            <div className="text-[14.5px] font-extrabold tracking-tight text-sidebar-brand">License Portal</div>
            <div className="text-[10.5px] font-semibold uppercase tracking-[.06em] text-sidebar-dim">Vendor-Konsole</div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 pb-5 pt-1.5" role="navigation">
          {NAV_ITEMS.map((item) => (
            <NavRow key={item.to} item={item} />
          ))}
        </nav>

        <div className="border-t border-sidebar-border p-3">
          <div className="flex items-center gap-2 rounded-lg bg-sidebar-active px-2.5 py-2">
            <span className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-ok shadow-[0_0_0_3px_color-mix(in_srgb,var(--ok)_18%,transparent)]" />
            <div className="text-[11.5px] font-semibold text-sidebar-text">Portal operativ</div>
          </div>
        </div>
      </aside>

      {/* ---------- Main column ---------- */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex h-[60px] shrink-0 items-center justify-between border-b border-border bg-surface px-[26px]">
          <h1 className="m-0 text-[17px] font-bold tracking-tight text-text">{title}</h1>
          {session && (
            <div className="flex items-center gap-2.5">
              <button
                onClick={toggleTheme}
                aria-label="Theme wechseln"
                title="Theme wechseln"
                className="grid h-[30px] w-[30px] place-items-center rounded-[7px] text-text3 transition-colors hover:bg-surface3 hover:text-text"
              >
                {theme === "light" ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
              </button>
              <div className="mx-0.5 h-6 w-px bg-border" />
              <div className="pl-0.5 text-right leading-tight">
                <div className="text-[12.5px] font-semibold text-text">{session.user.email}</div>
                <div className="text-[11px] text-text3">{session.user.role === "admin" ? "Administrator" : session.user.role}</div>
              </div>
              <div className="grid h-[34px] w-[34px] shrink-0 place-items-center rounded-full bg-gradient-to-br from-accent to-[#7c93f5] text-[13px] font-bold text-white">
                {session.user.email.slice(0, 2).toUpperCase()}
              </div>
              <button
                onClick={handleLogout}
                aria-label="Logout"
                title="Logout"
                data-testid="logout-button"
                className="grid h-[30px] w-[30px] place-items-center rounded-[7px] text-text3 transition-colors hover:bg-surface3 hover:text-text"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          )}
        </header>

        <main className="w-full flex-1 px-[30px] pb-[60px] pt-[26px]">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
